import { useEffect, useMemo, useState } from "react";
import {
	Button,
	Card,
	Checkbox,
	Col,
	Flex,
	InputNumber,
	Modal,
	notification,
	Row,
	Select,
	Spin,
	Table
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { KeyOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";

import { UserPermissionsAdminAPI } from "../../Api/UserPermissionsAdminAPI";
import {
	UserPermissionsAdminContact,
	UserPermissionsAdminModulePermission,
	UserPermissionsAdminOption,
	UserPermissionsAdminUserInfo,
	UserPermissionsAdminUserInfoErrors
} from "../../Models";

type UserPermissionsAdminProps = {
	baseURLExternal?: string;
	clientId: string;
	moduleRole: string;
	translations: any;
};

const BOOKBUILDING_SYSTEM_MASTER_ID = 1;

const emptyUserInfo: UserPermissionsAdminUserInfo = {
	id: 0,
	email: "",
	type: null,
	institution: null,
	phonePrefix: "",
	phoneNumber: ""
};

const emptyErrors: UserPermissionsAdminUserInfoErrors = {
	type: false,
	institution: false,
	phonePrefix: false,
	phoneNumber: false
};

const UserPermissionsAdmin = ({ baseURLExternal, clientId, moduleRole, translations }: UserPermissionsAdminProps) => {
	const t = (key: string, fallback: string) => translations?.[key] || fallback;
	const api = useMemo(() => UserPermissionsAdminAPI(baseURLExternal, clientId), [baseURLExternal, clientId]);
	const [loading, setLoading] = useState(false);
	const [contacts, setContacts] = useState<UserPermissionsAdminContact[]>([]);
	const [userTypes, setUserTypes] = useState<UserPermissionsAdminOption[]>([]);
	const [institutions, setInstitutions] = useState<UserPermissionsAdminOption[]>([]);
	const [idContact, setIdContact] = useState<number>(0);
	const [userInfo, setUserInfo] = useState<UserPermissionsAdminUserInfo>(emptyUserInfo);
	const [errors, setErrors] = useState<UserPermissionsAdminUserInfoErrors>(emptyErrors);
	const [modulesPermissions, setModulesPermissions] = useState<UserPermissionsAdminModulePermission[]>([]);
	const [selectedRowsKeys, setSelectedRowsKeys] = useState<number[]>([]);

	useEffect(() => {
		const loadInitialData = async () => {
			setLoading(true);
			try {
				const [contactsResponse, userTypesResponse, institutionsResponse] = await Promise.all([
					api.getContacts(),
					api.getUserTypes(),
					api.getInstitutions()
				]);

				setContacts(
					(Array.isArray(contactsResponse) ? contactsResponse : [])
						.filter((contact: any) => contact?.idContact && contact?.email)
						.sort((a: any, b: any) => String(a.email).localeCompare(String(b.email)))
				);
				setUserTypes(Array.isArray(userTypesResponse) ? userTypesResponse : []);
				setInstitutions(Array.isArray(institutionsResponse) ? institutionsResponse : []);
			} catch {
				notification.error({ message: t("adminErrorLoadingUserPermissionsData", "Error al cargar los permisos de usuario") });
			} finally {
				setLoading(false);
			}
		};

		loadInitialData();
	}, [api]);

	useEffect(() => {
		if (!idContact) {
			setModulesPermissions([]);
			setUserInfo(emptyUserInfo);
			setSelectedRowsKeys([]);
			return;
		}

		const loadContactPermissions = async () => {
			setLoading(true);
			try {
				const data = await api.getContactInformation(idContact, BOOKBUILDING_SYSTEM_MASTER_ID);
				if (!data) {
					Modal.confirm({
						title: t("adminNoUserFound", "No se ha encontrado usuario"),
						content: t("adminCreateUserForContact", "¿Quieres crear un usuario para este contacto?"),
						okText: t("adminCreate", "Crear"),
						cancelText: t("cancel", "Cancelar"),
						onOk: async () => {
							const contact = contacts.find((item) => item.idContact === idContact);
							if (!contact?.email) {
								notification.error({ message: t("adminContactNotFound", "Contacto no encontrado") });
								return;
							}

							setLoading(true);
							try {
								await api.createUser(contact.email, contact.idContact);
								notification.success({ message: t("adminUserCreated", "Usuario creado correctamente") });
								const createdUser = await api.getContactInformation(contact.idContact, BOOKBUILDING_SYSTEM_MASTER_ID);
								applyUserData(createdUser);
							} catch {
								notification.error({ message: t("adminUnableCreateUser", "No se ha podido crear el usuario") });
							} finally {
								setLoading(false);
							}
						},
						onCancel: () => {
							setModulesPermissions([]);
							setUserInfo(emptyUserInfo);
						}
					});
					return;
				}

				applyUserData(data);
			} catch {
				notification.error({ message: t("adminErrorLoadingPermissions", "Error al cargar los permisos de módulos") });
				setModulesPermissions([]);
				setUserInfo(emptyUserInfo);
			} finally {
				setLoading(false);
			}
		};

		loadContactPermissions();
	}, [api, contacts, idContact]);

	const applyUserData = (data: any) => {
		const phoneParts = String(data?.phoneNumber || "").split(" ");
		setUserInfo({
			id: data?.id ?? 0,
			email: data?.loginName ?? "",
			type: data?.idUserType ?? null,
			institution: data?.idInstitution ?? null,
			phonePrefix: phoneParts[0] ?? "",
			phoneNumber: phoneParts[1] ?? ""
		});
		setModulesPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
		setSelectedRowsKeys([]);
	};

	const onChangeUserInfo = (field: keyof UserPermissionsAdminUserInfo, value: number | string | null) => {
		setUserInfo((currentState) => ({
			...currentState,
			[field]: value
		}));
		setErrors((currentState) => ({
			...currentState,
			[field as keyof UserPermissionsAdminUserInfoErrors]: false
		}));
	};

	const validateUserInfo = () => {
		const nextErrors: UserPermissionsAdminUserInfoErrors = { ...emptyErrors };

		if (!userInfo.type) {
			nextErrors.type = true;
		}
		if (userInfo.type === 2 && !userInfo.institution) {
			nextErrors.institution = true;
		}
		if (userInfo.type === 1 && !userInfo.phonePrefix) {
			nextErrors.phonePrefix = true;
		}
		if (userInfo.type === 1 && !userInfo.phoneNumber) {
			nextErrors.phoneNumber = true;
		}
		if (userInfo.phoneNumber && !/^[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(String(userInfo.phoneNumber))) {
			nextErrors.phoneNumber = true;
		}
		if (userInfo.phoneNumber && !userInfo.phonePrefix) {
			nextErrors.phonePrefix = true;
		}

		setErrors(nextErrors);
		return !Object.values(nextErrors).some(Boolean);
	};

	const updateModuleRole = (recordKey: number, roleValue: number) => {
		setModulesPermissions((currentState) => currentState.map((permission) =>
			permission.key === recordKey
				? { ...permission, idRol: roleValue }
				: permission
		));
	};

	const handlePermissionChange = (record: UserPermissionsAdminModulePermission, roleType: "read" | "write" | "manage", checked: boolean) => {
		if (roleType === "read") {
			updateModuleRole(record.key, checked ? 1 : 0);
			return;
		}
		if (roleType === "write") {
			updateModuleRole(record.key, checked ? 2 : 1);
			return;
		}
		updateModuleRole(record.key, checked ? 3 : 2);
	};

	const selectedRows = useMemo(
		() => modulesPermissions.filter((permission) => selectedRowsKeys.includes(permission.key)),
		[modulesPermissions, selectedRowsKeys]
	);

	const applyBulkRole = (roleValue: number) => {
		if (selectedRows.length === 0) {
			return;
		}

		const selectedKeys = new Set(selectedRows.map((row) => row.key));
		setModulesPermissions((currentState) => currentState.map((permission) =>
			selectedKeys.has(permission.key)
				? { ...permission, idRol: roleValue }
				: permission
		));
	};

	const saveContact = async () => {
		if (!idContact || !validateUserInfo()) {
			notification.error({ message: t("adminFormErrorsBeforeSave", "Hay errores en el formulario, corrígelos antes de guardar") });
			return;
		}

		setLoading(true);
		try {
			const response = await api.saveContactInformation({
				id: userInfo.id,
				loginName: userInfo.email,
				idUserType: userInfo.type,
				idInstitution: userInfo.institution,
				phoneNumber: `${userInfo.phonePrefix || ""} ${userInfo.phoneNumber || ""}`.trim(),
				permissions: modulesPermissions
			});

			if (response?.success) {
				notification.success({ message: t("adminContactSaved", "Información del contacto guardada correctamente") });
			} else {
				notification.error({ message: response?.message || t("adminSaveError", "Se ha producido un error al guardar") });
			}
		} catch {
			notification.error({ message: t("adminSaveError", "Se ha producido un error al guardar") });
		} finally {
			setLoading(false);
		}
	};

	const resetPassword = async () => {
		if (!userInfo.email) {
			return;
		}

		setLoading(true);
		try {
			await api.requestPasswordReset(userInfo.email);
			notification.success({ message: t("adminResetPasswordSent", "Se ha enviado el token de reseteo de contraseña al email del usuario") });
		} catch {
			notification.error({ message: t("adminResetPasswordError", "No se ha podido enviar el token de reseteo de contraseña") });
		} finally {
			setLoading(false);
		}
	};

	const columns: ColumnsType<UserPermissionsAdminModulePermission> = [
		{
			title: t("adminPermissionApplication", "Aplicación"),
			dataIndex: "systemName",
			key: "systemName",
			width: 220
		},
		{
			title: t("adminPermissionModule", "Módulo"),
			dataIndex: "moduleName",
			key: "moduleName",
			width: 220
		},
		{
			title: t("adminPermissionDescription", "Descripción"),
			dataIndex: "description",
			key: "description"
		},
		{
			title: selectedRowsKeys.length > 1 ? (
				<Checkbox
					checked={selectedRows.length > 0 && selectedRows.every((row) => row.idRol > 0)}
					onChange={(event) => applyBulkRole(event.target.checked ? 1 : 0)}
				>
					{t("adminRead", "Lectura")}
				</Checkbox>
			) : t("adminRead", "Lectura"),
			key: "read",
			align: "center",
			width: 100,
			render: (_, record) => (
				<Checkbox
					checked={record.idRol > 0}
					onChange={(event) => handlePermissionChange(record, "read", event.target.checked)}
				/>
			)
		},
		{
			title: selectedRowsKeys.length > 1 ? (
				<Checkbox
					checked={selectedRows.length > 0 && selectedRows.every((row) => row.idRol > 1)}
					onChange={(event) => applyBulkRole(event.target.checked ? 2 : 1)}
				>
					{t("adminWrite", "Escritura")}
				</Checkbox>
			) : t("adminWrite", "Escritura"),
			key: "write",
			align: "center",
			width: 100,
			render: (_, record) => (
				<Checkbox
					checked={record.idRol > 1}
					onChange={(event) => handlePermissionChange(record, "write", event.target.checked)}
				/>
			)
		},
		{
			title: selectedRowsKeys.length > 1 ? (
				<Checkbox
					disabled={moduleRole !== "Manage"}
					checked={selectedRows.length > 0 && selectedRows.every((row) => row.idRol === 3)}
					onChange={(event) => applyBulkRole(event.target.checked ? 3 : 2)}
				>
					{t("adminManage", "Gestión")}
				</Checkbox>
			) : t("adminManage", "Gestión"),
			key: "manage",
			align: "center",
			width: 100,
			render: (_, record) => (
				<Checkbox
					disabled={moduleRole !== "Manage"}
					checked={record.idRol === 3}
					onChange={(event) => handlePermissionChange(record, "manage", event.target.checked)}
				/>
			)
		}
	];

	return (
		<Card title={t("adminUserPermissionsTitle", "Permisos de usuario")} className="templates-static-card">
			<Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 34, color: "black" }} spin />}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Flex gap={20} align="flex-end" justify="space-between" wrap="wrap">
							<Row gutter={[16, 16]} style={{ flex: 1 }}>
								<Col xs={24} md={8}>
									<label><strong>{t("user", "Usuario")} :</strong></label>
									<Select
										showSearch
										style={{ width: "100%" }}
										placeholder={t("adminSelectContact", "Selecciona un contacto")}
										value={idContact === 0 ? undefined : idContact}
										onChange={setIdContact}
										filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())}
										options={contacts.map((contact) => ({
											value: contact.idContact,
											label: contact.email
										}))}
									/>
								</Col>
								<Col xs={24} md={8}>
									<label><strong>{t("adminPermissionApplication", "Aplicación")} :</strong></label>
									<Select
										disabled
										style={{ width: "100%" }}
										value={BOOKBUILDING_SYSTEM_MASTER_ID}
										options={[{ value: BOOKBUILDING_SYSTEM_MASTER_ID, label: t("adminApplicationBookbuilding", "Bookbuilding") }]}
									/>
								</Col>
								<Col xs={24} md={8}>
									<label><strong>{t("adminUserType", "Tipo de usuario")} :</strong></label>
									<Select
										disabled={!idContact}
										style={{ width: "100%" }}
										value={userInfo.type ?? undefined}
										placeholder={t("adminSelectUserType", "Selecciona un tipo de usuario")}
										onChange={(value) => onChangeUserInfo("type", value)}
										status={errors.type ? "error" : ""}
										options={userTypes.map((userType) => ({
											value: userType.id,
											label: userType.name
										}))}
									/>
								</Col>
								<Col xs={24} md={8}>
									<label><strong>{t("institution", "Institución")} :</strong></label>
									<Select
										disabled={!idContact}
										style={{ width: "100%" }}
										value={userInfo.institution ?? undefined}
										placeholder={t("adminSelectInstitution", "Selecciona una institución")}
										onChange={(value) => onChangeUserInfo("institution", value)}
										status={errors.institution ? "error" : ""}
										options={[
											{ value: 2403, label: t("adminGenericInstitution", "Institución genérica") },
											...institutions.map((institution) => ({
												value: institution.id,
												label: institution.name
											}))
										]}
									/>
								</Col>
								<Col xs={24} md={8}>
									<label><strong>{t("adminPhonePrefix", "Prefijo telefónico")} :</strong></label>
									<Select
										disabled={!idContact}
										style={{ width: "100%" }}
										allowClear
										value={userInfo.phonePrefix || undefined}
										placeholder="+34"
										onChange={(value) => onChangeUserInfo("phonePrefix", value ?? null)}
										status={errors.phonePrefix ? "error" : ""}
										options={[{ value: "+34", label: "+34" }]}
									/>
								</Col>
								<Col xs={24} md={8}>
									<label><strong>{t("adminPhoneNumber", "Número de teléfono")} :</strong></label>
									<InputNumber<number>
										style={{ width: "100%" }}
										disabled={!idContact}
										value={userInfo.phoneNumber ? Number(userInfo.phoneNumber) : undefined}
										onChange={(value) => onChangeUserInfo("phoneNumber", value ? String(value) : null)}
										controls={false}
										status={errors.phoneNumber ? "error" : ""}
									/>
								</Col>
							</Row>
							<Flex gap={8} wrap="wrap">
								<Button onClick={resetPassword} disabled={!idContact}>
									<KeyOutlined /> {t("adminResetPassword", "Resetear contraseña")}
								</Button>
								<Button type="primary" onClick={saveContact} disabled={!idContact}>
									<SaveOutlined /> {t("adminSaveContact", "Guardar contacto")}
								</Button>
							</Flex>
						</Flex>
					</Col>
					<Col span={24}>
						<Table
							rowKey="key"
							bordered
							size="small"
							dataSource={modulesPermissions}
							columns={columns}
							pagination={{ pageSize: 20, pageSizeOptions: ["10", "20", "50"], showSizeChanger: true }}
							rowSelection={{
								selectedRowKeys: selectedRowsKeys,
								onChange: (keys) => setSelectedRowsKeys(keys as number[])
							}}
							scroll={{ x: true }}
						/>
					</Col>
				</Row>
			</Spin>
		</Card>
	);
};

export default UserPermissionsAdmin;
